import React, { useCallback, useMemo, type FC } from 'react'

import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue } from 'recoil'

import {
  BUTTON_VARIANT_SECONDARY,
  Button,
  Flex,
  FlexItem,
  InputText,
  LINK_VARIANT_DEFAULT,
  Link,
  Panel,
  Select,
} from 'mailup-web-components'

import FormContainer from 'Components/FormContainer'

import type { SearchState } from 'Pages/GroupsList/model'
import {
  changeSearchCallback,
  fetchResultsCallback,
} from 'Pages/GroupsList/recoil/callbacks/callbacks'
import { searchState } from 'Pages/GroupsList/recoil/state/state'

const Search: FC = () => {
  const intl = useIntl()

  const { groupType, searchBy } = useRecoilValue(searchState)

  const fetchResults = useRecoilCallback(fetchResultsCallback, [])
  const changeSearch = useRecoilCallback(changeSearchCallback, [])

  const handleSubmit = useCallback(
    (
      e: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>,
    ) => {
      e.preventDefault()

      void fetchResults({ pageNumber: 1 }, {}, true)
    },
    [fetchResults],
  )

  const handleReset = useCallback((): void => {
    void fetchResults(
      { pageNumber: 1, pageSize: 10 },
      { groupType: 'All', searchBy: '' },
      true,
    )
  }, [fetchResults])

  const handleChangeGroupType = useCallback(
    (v: SearchState['groupType']): void => {
      void changeSearch({ groupType: v })
    },
    [changeSearch],
  )

  const handleChangeSearchBy = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLInputElement>): void => {
      void changeSearch({ searchBy: value })
    },
    [changeSearch],
  )

  const options = useMemo(
    () => [
      {
        value: 'All',
        label: intl.formatMessage({ id: 'Load.GroupsList.GroupTypeAll' }),
      },
      {
        value: 'Normal',
        label: intl.formatMessage({ id: 'Load.GroupsList.GroupTypeNormal' }),
      },
      {
        value: 'Test',
        label: intl.formatMessage({ id: 'Load.GroupsList.GroupTypeTest' }),
      },
      {
        value: 'Form',
        label: intl.formatMessage({ id: 'Load.GroupsList.GroupTypeForm' }),
      },
    ],
    [intl],
  )

  return (
    <Panel data-mu-test="panel-search" margin="0">
      <FormContainer onSubmit={handleSubmit} canSubmit>
        <Flex alignItems="center" gap="16px">
          <FlexItem flexBasis="275px">
            <Select
              options={options}
              value={groupType}
              searchable={false}
              onChange={handleChangeGroupType}
              data-mu-test="search__group-type"
            />
          </FlexItem>

          <FlexItem flexGrow={1}>
            <InputText
              value={searchBy}
              onChange={handleChangeSearchBy}
              data-mu-test="search__search-by"
              maxLength="200"
              placeholder={intl.formatMessage({
                id: 'Load.GroupsList.GroupSearchByPlaceholder',
              })}
            />
          </FlexItem>

          <Button
            kind={BUTTON_VARIANT_SECONDARY}
            onClick={handleSubmit}
            data-mu-test="search__submit"
            type="submit"
          >
            {intl.formatMessage({ id: 'Load.MailupCommon.Search' })}
          </Button>
          <Link
            variant={LINK_VARIANT_DEFAULT}
            onClick={handleReset}
            data-mu-test="search__reset"
          >
            {intl.formatMessage({ id: 'Load.MailupCommon.Reset' })}
          </Link>
        </Flex>
      </FormContainer>
    </Panel>
  )
}

export default Search
